import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {add, deleteRecord, fetch, update} from '../services/PricesService';
import {Price} from '../types';
import {notifyError, notifySuccess} from '../../../components/ServiceNotifications';
import {pricesKeys} from './usePricesQuery';

/**
 * FNP-918 (§6): build the mutation payload by WHITELISTING the fields the API's
 * Input type declares.
 *
 * It was previously built by spreading the record and `delete`-ing `created`
 * and `updated`. Two problems with that shape:
 *
 *   1. It is a DENYLIST. Every field the UI adds to the record is silently sent
 *      to the API until someone remembers to `delete` it too. A whitelist
 *      cannot leak a field nobody thought about.
 *   2. The same pattern one layer up (`delete record.created` on the caller's
 *      object) MUTATES the live Formik/React Query object — which
 *      PricesService.update did literally, on the caller's record. See the note
 *      there.
 *
 * ⚠️ `recurring` is deliberately NOT sent. It is a nested object
 * (`{interval, intervalCount}`) that Stripe treats as immutable once a price
 * exists — the old edit path in RecordsTable had to `delete payload.recurring`
 * to make an update succeed at all, which is the tell. Omitting it from the
 * whitelist makes that permanent rather than something each call site
 * rediscovers.
 *
 * ⚠️ UNIT AMOUNT UNIT CONVERSION. The form collects currency units; the API
 * wants CENTS. `usePrices` divides by 100 on the way in, so this multiplies by
 * 100 on the way out. Getting this wrong is a money-path defect: a factor-100
 * error on a price is not a rendering glitch.
 */
const toPriceInput = (values: Price) => {
    const amount = Number(values.unitAmount);

    return {
        currency: values.currency,
        product: values.product,
        nickname: values.nickname,
        active: values.active,
        billingScheme: values.billingScheme,
        // Guarded: `parseInt(undefined)` is NaN, and sending NaN as an amount
        // is worse than sending nothing.
        unitAmount: Number.isFinite(amount) ? Math.round(amount * 100) : undefined,
    };
};

export const usePrice = (priceId?: string) => {
    const queryClient = useQueryClient();

    const priceQuery = useQuery({
        queryKey: pricesKeys.detail(priceId ?? ''),
        queryFn: () => fetch(priceId!),
        enabled: !!priceId,
    });

    const createMutation = useMutation({
        mutationFn: (values: Price) => add(toPriceInput(values)),
        onSuccess: () => {
            notifySuccess('Price created');
            queryClient.invalidateQueries({queryKey: pricesKeys.all});
        },
        onError: notifyError,
    });

    const updateMutation = useMutation({
        mutationFn: (values: Price) => {
            if (!priceId) throw new Error('priceId is required parameter');
            return update(priceId, {
                id: priceId,
                ...toPriceInput(values),
            } as unknown as Price);
        },
        onSuccess: () => {
            notifySuccess('Price updated');
            // Detail-page mutations invalidate BOTH the detail key and the
            // list key, or the list keeps showing the pre-edit values (§8).
            // `pricesKeys.all` is the prefix of both.
            queryClient.invalidateQueries({queryKey: pricesKeys.all});
        },
        onError: notifyError,
    });

    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!priceId) throw new Error('priceId is required parameter');
            return deleteRecord(priceId);
        },
        onSuccess: () => {
            notifySuccess('Price deleted');
            queryClient.invalidateQueries({queryKey: pricesKeys.all});
        },
        onError: notifyError,
    });

    return {
        record: priceQuery.data,
        loading: priceQuery.isLoading,
        fetchRecord: priceQuery.refetch,
        // ⚠️ mutateAsync, NOT mutate (§6/§7).
        //
        // `mutate` is fire-and-forget: it returns void, so the drawer's
        // `await handleSubmit(values); onClose();` awaits `undefined` and
        // closes IMMEDIATELY. A server-side rejection then destroys the
        // operator's input and toasts into a closed drawer, and Formik clears
        // `isSubmitting` on the same tick so the submit button's `disabled`
        // never engages. `mutateAsync` returns the real promise, which makes
        // both the drawer's await and the disabled-while-submitting wiring do
        // what they already claim to do.
        createRecord: createMutation.mutateAsync,
        updateRecord: updateMutation.mutateAsync,
        deleteRecord: deleteMutation.mutateAsync,
        createMutation,
        updateMutation,
        deleteMutation,
    };
};
